import { useEffect, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
  Dimmer,
  Grid,
  Loader,
  Table
} from "semantic-ui-react";
import { formatNumberToScientific } from "smart-decimal-format";
import { useGetDrawingChanges } from "../../../api/changeHistory";
import { useGetChangeReasonLog } from "../../../api/changeReasonLog";
import { useGetProductSummaryHistory } from "../../../api/productSummary";
import { AccordionTitleView, CommonModal, CommonTable } from "../../../components";
import { LoadingPlaceholder } from "../../../components/LoadingPlaceholder/LoadingPlaceholder";
import PaginationView from "../../../components/pagination/Pagination";
import { CALCULATED_VERSION_PREFIX, ProductSummaryHistoryTableCol, ProductSummaryTableCol } from "../../../config/constants";
import "../productSummary.scss";

const ProductSummaryDetailHistory = ({
  visibleModal,
  setVisibleModal,
  productId,
  salesUnitId
}: any) => {
  const [selectedHistory, setSelectedHistory] = useState<any>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(5);
  const [totalPages, setTotalPages] = useState<number>(10);
  const [totalSummaryHistory, setTotalSummaryHistory] = useState<number>(0);
  const [accordionEnabled, setAccordionEnabled] = useState<boolean>(true);
  const [showAllChanges, setShowAllChanges] = useState(false);
  const MAX_VISIBLE_CHANGES = 5;

  const handleClick = () => {
    setAccordionEnabled(!accordionEnabled);
  };

  const { data: productSummaryHistory, isFetching: isProductSummaryHistoryLoading } =
    useGetProductSummaryHistory({
      pageNumber,
      pageSize,
      productId,
      salesUnitId
    });

  const productSummaryHistoryData = productSummaryHistory?.result?.productSummaryHistory || [];

  const { data: drawingChanges, isFetching: isDrawingChangesLoading } = useGetDrawingChanges({
    from: selectedHistory?.changedTime?.from,
    to: selectedHistory?.changedTime?.to,
    sourceId: salesUnitId,
    ownerId: productId
  });

  const { data: changeReason, isFetching: isChangeReasonLoading } = useGetChangeReasonLog({
    from: selectedHistory?.changedTime?.from,
    to: selectedHistory?.changedTime?.to,
    sourceId: salesUnitId,
    ownerId: productId
  });

  useEffect(() => {
    if (productSummaryHistory) {
      setPageNumber(productSummaryHistory.page);
      setPageSize(productSummaryHistory.pageSize);
      setTotalSummaryHistory(productSummaryHistory.total);
    }
  }, [productSummaryHistory]);

  useEffect(() => {
    setTotalPages(Math.ceil(totalSummaryHistory / pageSize));
  }, [totalSummaryHistory, pageSize]);

  const loadHistoryTableData = () => {
    return productSummaryHistoryData.map((data: any, index: any) => {
      const { climateChange } = data;
      return (
        <Table.Row className="tbleR" key={index}>
          <Table.Cell>
            <p>
              {new Date(climateChange?.createdDate).toISOString().slice(0, 19).replace("T", " ")}
            </p>
          </Table.Cell>
          <Table.Cell>
            <p>{`${climateChange.measure?.toFixed(2)}`}</p>
          </Table.Cell>
          <Table.Cell>
            <p>{`${CALCULATED_VERSION_PREFIX} ${data?.calculateVersion}`}</p>
          </Table.Cell>
          <Table.Cell>
            <Grid>
              <Grid.Column
                computer={16}
                tablet={16}
                mobile={16}
                key={index}
                className="alignRight addViewDetails"
              >
                <div
                  key={index}
                  className={"customButtonViewSelect customButtonViewTitle"}
                  onClick={() => setSelectedHistory(data)}
                >
                  <p className={"customButtonTextSelect"}>Details</p>
                </div>
              </Grid.Column>
            </Grid>
          </Table.Cell>
        </Table.Row>
      );
    });
  };

  const loadTableData = () => {
    return selectedHistory?.totalImpact?.map((data: any, index: any) => {
      return (
        <Table.Row className="tbleR" key={index}>
          <Table.Cell>
            <p>{data.indicator}</p>
          </Table.Cell>
          <Table.Cell>
            <p>{formatNumberToScientific(data.measure, 5)}</p>
          </Table.Cell>
          <Table.Cell>
            <p>{data.unit}</p>
          </Table.Cell>
          <Table.Cell>
            <Grid>
              <Grid.Column
                computer={16}
                tablet={16}
                mobile={16}
                key={index}
                className="alignRight addViewDetails"
              ></Grid.Column>
            </Grid>
          </Table.Cell>
        </Table.Row>
      );
    });
  };

  return (
    <>
      <CommonModal
        modalOpenState={visibleModal}
        modalCloseState={() => {
          setVisibleModal(false);
          setSelectedHistory(null);
        }}
        centered={true}
        title="Product Summary History"
        style={{ overflow: "scroll" }}
      >
        {isDrawingChangesLoading || isChangeReasonLoading ? (
          <Dimmer active>
            <Loader content="Loading" />
          </Dimmer>
        ) : (
          <div className="tw-flex-1 tw-overflow-y-auto tw-px-6 tw-py-4">
            <Accordion>
              <AccordionTitle active={accordionEnabled} onClick={handleClick}>
                <AccordionTitleView accordionEnabled={accordionEnabled} title={"Summary History"} />
              </AccordionTitle>
              <AccordionContent active={accordionEnabled}>
                <CommonTable tableHeaderData={ProductSummaryHistoryTableCol}>
                  {isProductSummaryHistoryLoading ? (
                    <Table.Row>
                      <Table.Cell colSpan="7">
                        <LoadingPlaceholder />
                      </Table.Cell>
                    </Table.Row>
                  ) : (
                    loadHistoryTableData()
                  )}
                </CommonTable>
                <div style={{ marginTop: "10px" }}></div>
                <PaginationView
                  currentPageNo={pageNumber}
                  totalPages={totalPages}
                  pageSize={pageSize}
                  currentSelectPage={(page: number) => {
                    setPageNumber(page);
                  }}
                  changePageSize={(pageSize: number) => {
                    setPageNumber(1);
                    setPageSize(pageSize);
                  }}
                />
              </AccordionContent>
            </Accordion>

            {selectedHistory?.totalImpact?.length > 0 && (
              <Grid>
                <Grid.Column computer={16} tablet={16} mobile={16}>
                  <div className="drawingChangesBox">
                    <p className="drawingChangesBoxTitle">
                      {changeReason && `Change reason - ${changeReason}`}
                    </p>
                    <p className="drawingChangesBoxTitle">
                      {drawingChanges && drawingChanges.length > 0
                        ? "Below values were changed"
                        : "No changes were made to the mapping"}
                    </p>

                    {drawingChanges && drawingChanges?.length > 0 && (
                      <>
                        <ul className="drawingChangesList">
                          {(showAllChanges
                            ? drawingChanges
                            : drawingChanges.slice(0, MAX_VISIBLE_CHANGES)
                          ).map((change, index) => (
                            <li key={index}>{change}</li>
                          ))}
                        </ul>

                        {drawingChanges.length > MAX_VISIBLE_CHANGES && (
                          <p
                            className="drawingChangesToggleText"
                            onClick={() => setShowAllChanges(!showAllChanges)}
                          >
                            {showAllChanges ? "View less" : "View more"}
                          </p>
                        )}
                      </>
                    )}
                  </div>
                </Grid.Column>
                <Grid.Column computer={16} tablet={16} mobile={16} style={{ marginBottom: "20px" }}>
                  <CommonTable tableHeaderData={ProductSummaryTableCol}>
                    {loadTableData()}
                  </CommonTable>
                </Grid.Column>
              </Grid>
            )}
          </div>
        )}
      </CommonModal>
    </>
  );
};

export default ProductSummaryDetailHistory;
